import React, { forwardRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Grid } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks";
import { useLazyListAvailableBranchTallyUploadBranchesQuery } from "store/services/branchTallyUploadApi";

const INITIAL_STATE = { branch: "", isActive: true };

const BranchTallyUploadForm = forwardRef(
  ({ isBusy = false, branchTallyUploadDetail, onSubmit = () => {} }, ref) => {
    const navigate = useNavigate();
    const { viewEntity } = useAuth();
    const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: INITIAL_STATE });
    const [fetchBranches, { data: branches = [], isLoading: loadingBranches }] = useLazyListAvailableBranchTallyUploadBranchesQuery();
    const branchOptions = Array.isArray(branches) ? branches : branches?.data || [];
    const optionsWithCurrentBranch = branchTallyUploadDetail?.branch?._id
      && !branchOptions.some((option) => option._id === branchTallyUploadDetail.branch._id)
      ? [branchTallyUploadDetail.branch, ...branchOptions]
      : branchOptions;

    useEffect(() => {
      if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
        fetchBranches();
      }
    }, [fetchBranches, viewEntity?.entity?.account?._id, viewEntity?.entity?._id]);

    useEffect(() => {
      if (!branchTallyUploadDetail) return;
      reset({ branch: branchTallyUploadDetail.branch || "", isActive: branchTallyUploadDetail.isActive !== false });
    }, [branchTallyUploadDetail, reset]);

    const handleFormSubmit = (data) => {
      onSubmit({ branch: data.branch?._id || data.branch, ...(branchTallyUploadDetail && { isActive: data.isActive }) });
    };

    return (
      <AppForm loading={isBusy} ref={ref}>
        <AppForm.Content>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Controller
                name="branch"
                control={control}
                rules={{ required: "Please select a branch." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Branch"
                    value={field.value ?? null}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(option) => option?.name ? `${option.name}${option.code ? ` - ${option.code}` : ""}` : ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={optionsWithCurrentBranch}
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    helperText={errors?.branch?.message}
                    error={!!errors?.branch}
                  />
                )}
              />
            </Grid>
            {branchTallyUploadDetail && <Grid item xs={12} md={6}><Controller name="isActive" control={control} render={({ field }) => <AppSwitch {...field} label="Is Active?" />} /></Grid>}
          </Grid>
        </AppForm.Content>
        <AppForm.Actions>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit(handleFormSubmit)}>
            {branchTallyUploadDetail ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
    );
  },
);

BranchTallyUploadForm.propTypes = {
  isBusy: PropTypes.bool,
  branchTallyUploadDetail: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default BranchTallyUploadForm;
